import React, { useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Badge from "react-bootstrap/Badge";
import Spinner from "react-bootstrap/Spinner";
import { AiOutlineCheckCircle, AiOutlineRobot } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Header from "../../components/protected/Header";
import Footer from "../../components/footer";
import { auth } from "../../config/firebase";
import { useEntitlement } from "../../hooks/useEntitlement";
import { useTranslations } from "../../translations";
import PaymentMethodModal from "../../components/protected/PaymentMethodModal";

// Base de la API de IA (ai-blooms en Vercel), derivada de la URL de blooms.
const API_BASE = (
  process.env.REACT_APP_BLOOMS_API_URL || "https://ai-blooms.vercel.app/api/openai/blooms"
).replace(/\/api\/.*/, "");

const Subscription = () => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { ai, expiresAt, provider, loading } = useEntitlement();
  const [busy, setBusy] = useState(null);
  const [chooserPlan, setChooserPlan] = useState(null); // plan elegido -> abre el modal de método

  const plans = [
    {
      id: "monthly",
      name: t("planMonthly"),
      price: "$4.99",
      period: t("perMonth"),
      note: t("planMonthlyNote"),
      highlight: false,
    },
    {
      id: "annual",
      name: t("planAnnual"),
      price: "$39.99",
      period: t("perYear"),
      note: t("planAnnualNote"),
      highlight: true,
    },
  ];

  const features = [t("aiFeature1"), t("aiFeature2"), t("aiFeature3"), t("aiFeature4")];

  const openPortal = async () => {
    try {
      setBusy("portal");
      const idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      if (!idToken) throw new Error(t("sessionInvalid"));
      const res = await fetch(`${API_BASE}/api/portal`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ returnUrl: `${window.location.origin}/subscription` }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url; // Stripe Customer Portal
      } else {
        throw new Error(data.error || t("portalError"));
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: t("errorTitle"), text: err.message });
      setBusy(null);
    }
  };

  const startCheckout = async (plan) => {
    try {
      setBusy(plan);
      const idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      if (!idToken) throw new Error(t("sessionInvalid"));

      const res = await fetch(`${API_BASE}/api/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ plan, returnUrl: `${window.location.origin}/subscription` }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url; // redirige a Stripe Checkout
      } else {
        throw new Error(data.error || t("checkoutError"));
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: t("errorTitle"), text: err.message });
      setBusy(null);
    }
  };

  return (
    <div className="dashboard-wrapper">
      <Header />
      <main className="dashboard-main-content pb-5">
      <Container fluid className="py-4 mx-auto" style={{ maxWidth: 960 }}>
        <div className="text-center mb-5">
          <AiOutlineRobot size={48} style={{ color: "#6c5ce7" }} />
          <h2 className="mt-3 mb-2">{t("aiActivateTitle")}</h2>
          <p className="text-muted">{t("aiActivateSubtitle")}</p>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : ai ? (
          <Card className="text-center p-4 shadow-sm border-0" style={{ borderRadius: 16 }}>
            <Card.Body>
              <AiOutlineCheckCircle size={56} style={{ color: "#00b894" }} />
              <h4 className="mt-3">{t("aiActiveTitle")}</h4>
              <p className="text-muted mb-4">
                {t("aiActiveSubtitle")}
                {expiresAt
                  ? ` ${t("aiValidUntil")} ${new Date(expiresAt).toLocaleDateString()}.`
                  : ""}
              </p>
              {provider === "stripe" ? (
                <Button
                  variant="outline-primary"
                  onClick={openPortal}
                  disabled={busy !== null}
                >
                  {busy === "portal" ? <Spinner animation="border" size="sm" /> : t("manageSubscription")}
                </Button>
              ) : (
                <p className="small text-muted mb-0">{t("manageLocalNote")}</p>
              )}
            </Card.Body>
          </Card>
        ) : (
          <Row className="g-4 justify-content-center">
            {plans.map((plan) => (
              <Col md={6} key={plan.id}>
                <Card
                  className="h-100 shadow-sm border-0"
                  style={{
                    borderRadius: 16,
                    outline: plan.highlight ? "2px solid #6c5ce7" : "none",
                  }}
                >
                  <Card.Body className="d-flex flex-column p-4">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h5 className="mb-0">{plan.name}</h5>
                      {plan.highlight && <Badge bg="success">{t("bestValue")}</Badge>}
                    </div>
                    <div className="mb-3">
                      <span style={{ fontSize: 36, fontWeight: 700 }}>{plan.price}</span>
                      <span className="text-muted">{plan.period}</span>
                    </div>
                    <ul className="list-unstyled mb-4 flex-grow-1">
                      {features.map((f) => (
                        <li key={f} className="mb-2 d-flex align-items-start gap-2">
                          <AiOutlineCheckCircle
                            size={18}
                            style={{ color: "#00b894", marginTop: 3, flexShrink: 0 }}
                          />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      variant={plan.highlight ? "primary" : "outline-primary"}
                      size="lg"
                      disabled={busy !== null}
                      onClick={() => setChooserPlan(plan.id)}
                    >
                      {t("activateAi")}
                    </Button>
                    <small className="text-muted text-center mt-2">{plan.note}</small>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Container>
        <Footer logged={true} />
      </main>

      <PaymentMethodModal
        show={chooserPlan !== null}
        onHide={() => setChooserPlan(null)}
        planLabel={chooserPlan === "annual" ? t("planAnnual") : t("planMonthly")}
        priceLabel={chooserPlan === "annual" ? "$39.99" : "$4.99"}
        onSelectCard={() => {
          const p = chooserPlan;
          setChooserPlan(null);
          startCheckout(p);
        }}
        onSelectLocal={() => {
          const p = chooserPlan;
          setChooserPlan(null);
          navigate(`/subscription/local?plan=${p}`);
        }}
      />
    </div>
  );
};

export default Subscription;
